import * as React from 'react';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { StylesProps, ThemeType } from 'theme/jss-types';
import { commonStyleSheet } from 'library/common-styles';
import { useTranslation } from 'react-i18next';
import {
    Col,
    Form,
    Input,
    InputNumber,
    Radio,
    Select,
    DatePicker,
} from 'antd';
import moment from 'moment';

const styles = (theme: ThemeType) => ({
    formItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'left',
        '& .ant-checkbox + span': {
            color: '#111111',
            fontSize: 14,
            fontWeight: 600,
        },
    },
    title: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
        width: '100%',
        marginBottom: 4,
        marginLeft: 4,
    },
    referenceItem: {
        display: 'flex',
        flexDirection: 'column',
    },
    contentType: {
        ...commonStyleSheet(theme).flexRow,
        justifyContent: 'space-between',
        '& .ant-form-item': {
            marginBottom: 0,
        },
    },
});

interface IProps extends StylesProps<ReturnType<typeof styles>>, ReturnType<typeof mapStateToProps> {
    params: any;
}

const CustomReferenceNumberField = (props: IProps) => {
    const { t } = useTranslation();
    const { classes, params } = props;
    const {
        currentCustomField,
        id: pieceIndex,
        key,
        getValidationRules,
        getIsRequiredValidation,
        disabledFields = {},
    } = params;
    const currentField = currentCustomField[0];
    const isPieceLevel = pieceIndex !== null && pieceIndex !== undefined;
    const getFieldDisabled = (fieldData: any) => {
        const fieldKey = isPieceLevel ? key : fieldData?.id;
        return !!disabledFields[fieldKey || ''];
    };

    const renderTitle = (fieldData: any) => {
        return (
            <div
                className={classes.title}
            >
                {isPieceLevel ? t(`Piece ${fieldData.prettyName}`) : t(fieldData.prettyName)}
                {getIsRequiredValidation(isPieceLevel ? key : fieldData.id, isPieceLevel) && '*'}
            </div>
        );
    };

    const renderInput = (fieldData: any) => {
        return (
            <div className={classes.referenceItem}>
                <Form.Item
                    name={isPieceLevel ? key : fieldData.id}
                    className={classes.contentType}
                    valuePropName="value"
                    rules={getValidationRules({ ...fieldData, key: isPieceLevel ? key : fieldData.id }, isPieceLevel)}
                >
                    <Input
                        type={fieldData.inputType || 'text'}
                        placeholder={t('enter_value')}
                        disabled={getFieldDisabled(fieldData)}
                    />
                </Form.Item>
            </div>
        );
    };

    const renderStringArray = (fieldData: any) => {
        return (
            <div className={classes.referenceItem}>
                <Form.Item
                    name={isPieceLevel ? key : fieldData.id}
                    className={classes.contentType}
                    valuePropName="value"
                    rules={getValidationRules({ ...fieldData, key: isPieceLevel ? key : fieldData.id }, isPieceLevel)}
                >
                    <Select
                        mode="tags"
                        style={{ width: '100%' }}
                        placeholder={t('enter_value')}
                        allowClear
                        tokenSeparators={[',']}
                        disabled={getFieldDisabled(fieldData)}
                    />
                </Form.Item>
            </div>
        );
    };

    const renderNumberInput = (fieldData: any) => {
        return (
            <div className={classes.referenceItem}>
                <Form.Item
                    name={isPieceLevel ? key : fieldData.id}
                    className={classes.contentType}
                    valuePropName="value"
                    rules={getValidationRules({ ...fieldData, key: isPieceLevel ? key : fieldData.id }, isPieceLevel)}
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        placeholder={t('enter_value')}
                        disabled={getFieldDisabled(fieldData)}
                    />
                </Form.Item>
            </div>
        );
    };

    const renderBoolean = (fieldData: any) => {
        return (
            <Form.Item
                name={isPieceLevel ? key : fieldData.id}
                valuePropName="value"
                initialValue={false}
            >
                <Radio.Group
                    options={[{ value: false, label: t('false') }, { value: true, label: t('true') }]}
                    disabled={getFieldDisabled(fieldData)}
                />
            </Form.Item>
        );
    };

    const renderDropdown = (fieldData: any) => {
        if (!(fieldData?.formUIComponent && fieldData?.formUIComponent?.type === 'dropdown')) {
            return <></>;
        }
        const options = fieldData?.formUIComponent?.values;
        if (Array.isArray(options) && options.length) {
            const isMultiple = fieldData?.formUIComponent?.multiselect ? { mode: 'multiple' } : {};
            return (
                <Form.Item
                    name={isPieceLevel ? key : fieldData.id}
                    initialValue={fieldData.defaultValue}
                    valuePropName={fieldData.valuePropName}
                    rules={getValidationRules(fieldData, isPieceLevel)}
                >
                    <Select
                        placeholder={t('select_values')}
                        options={options}
                        {...isMultiple}
                        allowClear
                        disabled={getFieldDisabled(fieldData)}
                    />
                </Form.Item>
            );
        }
        return <></>;
    };

    const renderDateTime = (fieldData: any, type: string) => {
        return (
            <Form.Item
                name={isPieceLevel ? key : fieldData.id}
                initialValue={fieldData.defaultValue ? moment(fieldData.defaultValue) : undefined}
                rules={getValidationRules(fieldData, isPieceLevel)}
                getValueProps={(value) => ({
                    value: value ? moment(value) : undefined,
                })}
                normalize={(value) => (value ? value.valueOf() : null)}
            >
                <DatePicker
                    placeholder={type === 'datetime' ? t('select_date_time') : t('select_date')}
                    format="YYYY-MM-DD HH:mm:ss"
                    disabled={getFieldDisabled(fieldData)}
                    showTime={type === 'datetime'
                        && {
                            format: 'HH:mm:ss',
                            defaultValue: moment('00:00:00', 'HH:mm:ss'),
                        }}
                />
            </Form.Item>
        );
    };

    const renderFormItem = (
        fieldData: any,
    ) => {
        let dataType = fieldData?.formUIComponent?.type;
        if (!(dataType && dataType === 'dropdown')) {
            dataType = fieldData?.dataType;
        }
        switch (dataType) {
            case 'stringArray': return renderStringArray(fieldData);
            case 'string': return renderInput(fieldData);
            case 'number': return renderNumberInput(fieldData);
            case 'boolean': return renderBoolean(fieldData);
            case 'dropdown': return renderDropdown(fieldData);
            case 'date': return renderDateTime(fieldData, 'date');
            case 'datetime': return renderDateTime(fieldData, 'datetime');
            default: return 'Input is not defined';
        }
    };

    if (currentField?.formUIComponent?.type === 'hyperlink') {
        return <></>;
    }

    return (
        <Col span={24} xs={24} sm={24} md={12}>
            <div className={classes.formItem}>
                {renderTitle(currentField)}
                {renderFormItem(currentField)}
            </div>
        </Col>
    );
};

const mapStateToProps = () => {
    return {};
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
};

export default GenericHoc(hocConfig)(CustomReferenceNumberField);
